import { environment } from '../../../environments/environment';

/** Cookie names omit `:` so they are valid across localhost ports. */
export const SHARED_SESSION_COOKIE = {
  accessToken: 'eventq_accessToken',
  email: 'eventq_sessionEmail',
  role: 'eventq_sessionRole',
  displayName: 'eventq_sessionDisplayName',
  browserSessionId: 'eventq_browserSessionId',
  expiresAt: 'eventq_sessionExpiresAt',
} as const;

export const ACCESS_TOKEN_STORAGE_KEY = 'eventq:accessToken';
export const SESSION_EMAIL_STORAGE_KEY = 'eventq:sessionEmail';
export const SESSION_ROLE_STORAGE_KEY = 'eventq:sessionRole';
export const SESSION_DISPLAY_NAME_STORAGE_KEY = 'eventq:sessionDisplayName';
export const BROWSER_SESSION_ID_STORAGE_KEY = 'eventq:browserSessionId';
export const SESSION_EXPIRES_AT_STORAGE_KEY = 'eventq:sessionExpiresAt';

export interface SharedSessionSnapshot {
  accessToken: string;
  email: string | null;
  role: string | null;
  displayName: string | null;
}

/** Cookies ignore port, so EventQ :4200 and Resolve :4201 share one login on localhost. */
const COOKIE_SUFFIX = 'path=/; SameSite=Lax';

function cookieWriteSuffix(): string {
  const domain = environment.sessionCookieDomain.trim();
  return domain ? `Domain=${domain}; ${COOKIE_SUFFIX}` : COOKIE_SUFFIX;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const value = part.trim();
    if (value.startsWith(prefix)) {
      return decodeURIComponent(value.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name: string, value: string | null): void {
  if (typeof document === 'undefined') {
    return;
  }
  if (value == null || value === '') {
    document.cookie = `${name}=; ${cookieWriteSuffix()}; max-age=0`;
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(value)}; ${cookieWriteSuffix()}`;
}

/** Stable per-browser id shared by EventQ + Resolve (cookie + localStorage). */
export function getOrCreateBrowserSessionId(): string {
  const existing =
    localStorage.getItem(BROWSER_SESSION_ID_STORAGE_KEY) ??
    readCookie(SHARED_SESSION_COOKIE.browserSessionId);
  if (existing) {
    localStorage.setItem(BROWSER_SESSION_ID_STORAGE_KEY, existing);
    writeCookie(SHARED_SESSION_COOKIE.browserSessionId, existing);
    return existing;
  }
  const created =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `browser-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(BROWSER_SESSION_ID_STORAGE_KEY, created);
  writeCookie(SHARED_SESSION_COOKIE.browserSessionId, created);
  return created;
}

export function writeSharedSession(session: SharedSessionSnapshot): void {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, session.accessToken);
  writeCookie(SHARED_SESSION_COOKIE.accessToken, session.accessToken);
  if (session.email) {
    localStorage.setItem(SESSION_EMAIL_STORAGE_KEY, session.email);
    writeCookie(SHARED_SESSION_COOKIE.email, session.email);
  }
  if (session.role) {
    localStorage.setItem(SESSION_ROLE_STORAGE_KEY, session.role);
    writeCookie(SHARED_SESSION_COOKIE.role, session.role);
  }
  if (session.displayName) {
    localStorage.setItem(SESSION_DISPLAY_NAME_STORAGE_KEY, session.displayName);
    writeCookie(SHARED_SESSION_COOKIE.displayName, session.displayName);
  }
  getOrCreateBrowserSessionId();
}

export function readSharedSession(): SharedSessionSnapshot | null {
  const accessToken =
    localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) ?? readCookie(SHARED_SESSION_COOKIE.accessToken);
  if (!accessToken) {
    return null;
  }
  return {
    accessToken,
    email: localStorage.getItem(SESSION_EMAIL_STORAGE_KEY) ?? readCookie(SHARED_SESSION_COOKIE.email),
    role: localStorage.getItem(SESSION_ROLE_STORAGE_KEY) ?? readCookie(SHARED_SESSION_COOKIE.role),
    displayName:
      localStorage.getItem(SESSION_DISPLAY_NAME_STORAGE_KEY) ??
      readCookie(SHARED_SESSION_COOKIE.displayName),
  };
}

export function persistSessionExpiresAt(expiresAtMs: number | null): void {
  if (expiresAtMs == null) {
    localStorage.removeItem(SESSION_EXPIRES_AT_STORAGE_KEY);
    writeCookie(SHARED_SESSION_COOKIE.expiresAt, null);
    return;
  }
  const value = String(expiresAtMs);
  localStorage.setItem(SESSION_EXPIRES_AT_STORAGE_KEY, value);
  writeCookie(SHARED_SESSION_COOKIE.expiresAt, value);
}

export function readSessionExpiresAt(): number | null {
  const raw =
    localStorage.getItem(SESSION_EXPIRES_AT_STORAGE_KEY) ??
    readCookie(SHARED_SESSION_COOKIE.expiresAt);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function clearSharedSession(): void {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(SESSION_EMAIL_STORAGE_KEY);
  localStorage.removeItem(SESSION_ROLE_STORAGE_KEY);
  localStorage.removeItem(SESSION_DISPLAY_NAME_STORAGE_KEY);
  localStorage.removeItem(SESSION_EXPIRES_AT_STORAGE_KEY);
  writeCookie(SHARED_SESSION_COOKIE.accessToken, null);
  writeCookie(SHARED_SESSION_COOKIE.email, null);
  writeCookie(SHARED_SESSION_COOKIE.role, null);
  writeCookie(SHARED_SESSION_COOKIE.displayName, null);
  writeCookie(SHARED_SESSION_COOKIE.expiresAt, null);
  // Keep browserSessionId so re-login from this browser reuses the same identity.
}
